import React, {useEffect, useState} from "react";
import {fetchPriceForPlanWithPromo} from "../../../Api/Sales/PricesApi";
import {ApplicationContext} from "../../../ApplicationContext";
import {SimOnlyPlan} from "../../../Model/Sales/SimOnlyPlan";
import {PlanPrice} from "../../../Model/Sales/PlanPrice";
import {UserDetails} from "../../../Model/User/UserDetails";
import {isRequestError, RequestError} from "../../../Model/RequestError";
import {fetchSimOnlyPlanByCode, fetchSimOnlyPlanByPlanCodeAndPromoCode} from "../../../Api/Sales/SimOnlyPlanApi";
import {SALES_PORTAL_ROUTES} from "../../../Routes/SalesPortalRoutes";
import {useNavigate} from "react-router-dom";

const PromoCodeComponent = (props: {componentData: {
        readonly selectedPlan: SimOnlyPlan;
        readonly selectedPlanPriceWithPromo: PlanPrice | null;
        readonly userDetails?: UserDetails;
    },
    readonly useQueryParamPromo?: boolean,
    readonly promoCode?: string | null
    setPromoCode?: (promo: string | null) => void,
    setSelectedPlanPriceWithPromo: ((planPrice: PlanPrice | null) => void | null),
    setSelectedPlan: (simOnlyPlan: SimOnlyPlan) => void
}): JSX.Element | null => {
    const initialButtonState = {
        text: "Apply",
        className: 'sp-link-button-secondary promo-button',
        disabled: false,
    }
    const [inputValue, setInputValue] = useState(props.promoCode ?? '')
    const [inputDisabled, setInputDisabled] = useState(false)
    const [inputClassName, setInputClassName] = useState('form-control promo-input')
    const [buttonState, setButtonState] = useState(initialButtonState);

    const [isExternalUpdate, setIsExternalUpdate] = useState(false);

    const applicationContext = React.useContext(ApplicationContext)
    const navigate = useNavigate()

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setInputValue(event.target.value)
    }
    const staffPromoCode = applicationContext.appConfig.signupConfiguration.staffPromo
    const forcedToStaffPromo = !!props.componentData.userDetails?.hybris?.cardNumber?.startsWith("2660")

    const queryParams = new URLSearchParams(window.location.search);

    let handleClick = async () => {
        removePromoCodeFromLocalStorage();

        if (buttonState.text === "Apply") {
            await handleApply(true);
        } else {
            await handleRemove(true)
        }
    };

    useEffect(() => {
        setInputValue(props.promoCode ?? '')
        setIsExternalUpdate(true)
    }, [props.promoCode])

    useEffect(() => {
        if (!isExternalUpdate) return;
        if (inputValue == '') {
            handleRemove(false)
        } else {
            handleApply(false)
        }
        setIsExternalUpdate(false);
    }, [inputValue, isExternalUpdate])

    async function handleApply(click: boolean) {
        const promoCode = inputValue;
        try {
            // If promo is the same as for staff, but customer is not staff
            if (!forcedToStaffPromo && staffPromoCode === promoCode) {
                applyInvalidPromoStyles(promoCode)
                return;
            }

            const maybePlanPrice = await fetchPlanPriceWithPromo(promoCode)

            if (isRequestError(maybePlanPrice)) {
                applyInvalidPromoStyles(promoCode)
            } else {
                props.setSelectedPlanPriceWithPromo(maybePlanPrice)
                if (click && props.setPromoCode) {
                    props.setPromoCode(promoCode)
                }
                applyPromoStyles(promoCode)
                addPromoCodeToQueryParam(promoCode)
            }
        } catch (error) {
            applyInvalidPromoStyles(promoCode)
        }
    }

    async function handleRemove(click: boolean) {
        try {
            const selectedPlan = await fetchSimOnlyPlanByPlanCodeAndPromoCode(
                applicationContext.reseller,
                applicationContext.salesPortalApiDomain,
                props.componentData.selectedPlan.packageCode,
                null
            );

            if (isRequestError(selectedPlan)) {
                navigate(SALES_PORTAL_ROUTES.InternalError(applicationContext.urlContext))
            } else {
                clearPromoStyles()
                deletePromoCodeFromQueryParam()

                if (click && props.setPromoCode) {
                    props.setPromoCode(null)
                }
            }
        } catch (error) {
            navigate(SALES_PORTAL_ROUTES.InternalError(applicationContext.urlContext))
        }
    }

    async function handleQueryParamPromoCode(queryParamPromoCode: string | null) {
        if (queryParamPromoCode === null) return;
        if (!props.useQueryParamPromo) return;

        if (forcedToStaffPromo) {
            if (staffPromoCode === null) return;
            const planPrice = await fetchPlanPriceWithPromo(staffPromoCode);
            await handlePlanPriceWithPromoResponse(planPrice, queryParamPromoCode);
            handleForcedToStaffPromo()
        } else {
            const planPrice = await fetchPlanPriceWithPromo(queryParamPromoCode);
            await handlePlanPriceWithPromoResponse(planPrice, queryParamPromoCode);
        }
    }
    async function fetchPlanPriceWithPromo(promoCode: string) {
        return await fetchPriceForPlanWithPromo(
            applicationContext.reseller,
            applicationContext.salesPortalApiDomain,
            props.componentData.selectedPlan.packageCode,
            promoCode
        );
    }
    async function handlePlanPriceWithPromoResponse(planPrice: PlanPrice | RequestError, promoCode: string) {
        try {
            if (isRequestError(planPrice)) {
                applyInvalidPromoStyles(promoCode)
                deletePromoCodeFromQueryParam()
            } else {
                applyPromoStyles(planPrice.promoCode ?? '' )

                props.setSelectedPlanPriceWithPromo(planPrice)
                addPromoCodeToQueryParam(promoCode);
            }
        } catch (e) {
            applyInvalidPromoStyles(promoCode)
            deletePromoCodeFromQueryParam()
        }
    }

    function addPromoCodeToQueryParam(promoCode: string) {
        if (!props.useQueryParamPromo) return;
        queryParams.set(PROMO_CODE_QUERY_NAME, promoCode);
        const newUrl = `${window.location.origin}${window.location.pathname}?${queryParams.toString()}`;
        window.history.replaceState(null, '', newUrl);
    }

    function deletePromoCodeFromQueryParam() {
        if (!props.useQueryParamPromo) return;
        queryParams.delete(PROMO_CODE_QUERY_NAME);
        const newUrl = `${window.location.origin}${window.location.pathname}?${queryParams.toString()}`;
        window.history.replaceState(null, '', newUrl);
    }

    function handleInitialRender() {
        let isPlanPriceWithPromo = props.componentData.selectedPlanPriceWithPromo !== null
        if (isPlanPriceWithPromo) {
            applyPromoStyles(props.componentData.selectedPlanPriceWithPromo?.promoCode ?? '')
        }
    }
    function applyPromoStyles(promoCode: string) {
        setInputValue(promoCode)
        setInputClassName('form-control is-valid promo-input')
        setInputDisabled(true)
        setButtonState({ ...buttonState, className: 'sp-link-button-secondary promo-button', text: 'Remove' });
        if (forcedToStaffPromo) {
            // Disable remove button as if customer is staff, they can only have staff promo
            setButtonState(prevState => ({ ...prevState, disabled: true }));
        }
    }
    function applyInvalidPromoStyles(promoCode: string){
        setInputValue(promoCode)
        setInputClassName('form-control is-invalid promo-input')
        setInputDisabled(false)
        setButtonState({ ...buttonState, text: 'Apply', disabled: false});
    }
    function clearPromoStyles() {
        setInputValue('')
        setInputClassName('form-control promo-input')
        setInputDisabled(false)
        setButtonState(initialButtonState)
    }

    const handlePromoCodeInLocalStorage = () => {
        const promoCode = getPromoCodeFromLocalStorage()
        if (promoCode !== null){
            addPromoCodeToQueryParam(promoCode)
            removePromoCodeFromLocalStorage()
        }
    };
    const handleForcedToStaffPromo = () => {
        // Remove promoCode from url param as forcing staff promo
        if (forcedToStaffPromo){
            deletePromoCodeFromQueryParam()
        }
    };
    useEffect(() => {
        removePromoCodeFromLocalStorage()
        handlePromoCodeInLocalStorage()
        let queryParamPromoCode = queryParams.get('promo')
        let renderFromQueryParamPromoCode = queryParamPromoCode !== null &&
            props.componentData.selectedPlanPriceWithPromo?.promoCode !== queryParamPromoCode

        if (renderFromQueryParamPromoCode){
            handleQueryParamPromoCode(queryParamPromoCode);
        } else {
            handleInitialRender();
        }
        return () => {
        };
    }, []);

    return forcedToStaffPromo ? null :
        <div className={"input-group mb-3"}>
            <style>
                {`
                .promo-input {
                  border-radius: 20px !important;
                  margin-right: 10px;
                }
                .form-control:focus {
                  border-color: #1E3C91 !important;
                  box-shadow: 0 0 0 .25rem rgba(13,110,253,.25) !important;
                }
                .promo-button {
                  border-radius: 999px !important;
                  padding-left: 20px;
                  padding-right: 20px;
                  background-color: white;
                }
                input.form-control.promo-input::placeholder {
                  font-size: 14px;
                }
                `}
            </style>
            <input disabled={inputDisabled} type={"text"} className={inputClassName} placeholder={"Got a promo code? Enter it here"}
                   aria-label={"Got a promo code? Enter it here"} value={inputValue} onChange={handleChange}/>
            <button className={buttonState.className} type={"button"} id={"promoCodeBtn"} onClick={handleClick}
                    disabled={buttonState.disabled}>{buttonState.text}</button>
            <div id="validationPromoCode" className="invalid-feedback">
                Promo code is invalid.
            </div>
            <div className="valid-feedback">
                Promo code applied!
            </div>
        </div>
}

const PROMO_CODE_QUERY_NAME: string = "promo"
export const setPromoCodeToLocalStorage = () => {
    const queryParams = new URLSearchParams(window.location.search);
    const promoCode = queryParams.get(PROMO_CODE_QUERY_NAME)
    if (promoCode !== null){
        localStorage.setItem(PROMO_CODE_QUERY_NAME, promoCode)
    }
}
const getPromoCodeFromLocalStorage = () => {
    return localStorage.getItem(PROMO_CODE_QUERY_NAME)
}
const removePromoCodeFromLocalStorage = () => {
    localStorage.removeItem(PROMO_CODE_QUERY_NAME)
}
export default PromoCodeComponent; setPromoCodeToLocalStorage()